import { batch } from 'react-redux';

import {
  setFirstMessageChatsBlocked,
  setFirstMessageFailed,
  setFirstMessageNeedCaptcha,
  setFirstMessageNeedConfirmPhone,
  setFirstMessagePending,
  setFirstMessageSent,
  setFirstMessageUserChatsDisabled,
  setFirstMessageValidationError,
} from './actions';
import { trackOpenNoLinksPopup, trackFirstMessageSent, trackOpenForbiddenTextPopup } from './tracking';
import { Messages } from './types';
import { BLOB_ID } from '../../constants/blobId';
import { EChatType } from '../../repositories/chats/entities/create_custom_chat/V1CustomChatSendMessageRequestSchema';
import { EPayload } from '../../repositories/chats/entities/message/MessageRequestV2Schema';
import { IErrorResponseSchema } from '../../repositories/chats/entities/schemas/ErrorResponseSchema';
import { EStatus } from '../../repositories/chats/entities/synchronization/SyncChatSchema';
import { fetchCustomChatCreateChat } from '../../repositories/chats/v1/custom-chat/create-chat';
import { fetchMessagesOffer, TMessagesOfferResponse } from '../../repositories/chats/v3/messages/offer';
import {
  TSendFirstMessageToAgentResponse,
  fetchSendFirstMessageToAgent,
} from '../../repositories/chats/v3/send-first-message-to-agent';
import { hintQuestionsSelector } from '../../selectors/hintQuestionsSelector';
import { showAlert } from '../../slices/alertSlice';
import { EAlert } from '../../types/alert';
import { ICustomChatParams } from '../../types/customChatParams';
import { EEventQueueEvents } from '../../types/eventQueue';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getCustomChatContext } from '../../utils/customChat';
import { isInIframe } from '../../utils/isInIframe';

interface ISendFirstMessageProps {
  /** Токен капчи(для проверки валидности, только desktop) **/
  captchaToken?: string;
  /** Идентификатор объявления **/
  offerId?: number;
  /** Параметры чата по кастомной сущности. **/
  customChatParams?: ICustomChatParams;
  /** Текст сообщения **/
  text: string;
  /** Идентификатор агента **/
  agentId?: number;
  /** Пэйлоад для субсидированной ипотеки **/
  payload?: string;
  isAnalyticsKeyDisabled?: boolean;
}

/**
 * Отправка первого сообщения пользователем UI
 */
export const sendFirstMessage =
  (parameters: ISendFirstMessageProps): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { httpApi, logger, telemetry }) => {
    const state = getState();
    const { openedChats, user, parentSource, isEarlyAccess } = state;

    const { ga, newChatId } = openedChats[BLOB_ID];

    dispatch(setFirstMessagePending());

    let res;

    const { offerId, text, captchaToken, agentId, payload, isAnalyticsKeyDisabled, customChatParams } = parameters;

    try {
      if (offerId) {
        res = (await fetchMessagesOffer({
          httpApi,
          parameters: {
            offerId,
            text,
            captchaToken,
            payload: payload ? (payload as EPayload) : undefined,
          },
        })) as TMessagesOfferResponse;
      }

      if (agentId) {
        res = (await fetchSendFirstMessageToAgent({
          httpApi,
          parameters: {
            userId: agentId,
            text,
            captchaToken,
          },
        })) as TSendFirstMessageToAgentResponse;
      }

      if (customChatParams) {
        res = (await fetchCustomChatCreateChat({
          httpApi,
          parameters: {
            entityId: customChatParams.entityId,
            chatType: customChatParams.entityType as EChatType,
            receiverUserId: customChatParams.receiverUserId,
            context: getCustomChatContext(customChatParams.context, logger),
            text,
            captchaToken,
          },
        })) as TSendFirstMessageToAgentResponse;
      }

      const { statusCode, response } = res || {};

      if (!response) {
        return;
      }

      if (statusCode === 200) {
        if (response.chat && response.chat.chatId) {
          telemetry.increment(ETelemetryEvents.ChatFirstMessageSendSuccess);

          const hintQuestions = hintQuestionsSelector(state);

          const { analyticsKey = null } = hintQuestions.find(({ messageText }) => messageText === text) || {};

          // значит уже отправлена метрика по этому чату
          if (newChatId !== response.chat.chatId) {
            trackFirstMessageSent({
              ga,
              users: response.chat.users,
              offerId: response.chat.offer?.id,
              agentId,
              userId: user.userId,
              analyticsKey: isAnalyticsKeyDisabled ? null : analyticsKey,
              pageTypeSource: parentSource,
              isEarlyAccess,
              chatId: response.chat.chatId,
            });
          }

          dispatch(setFirstMessageSent({ chat: response.chat }));
          const isIframe = isInIframe();
          if (isIframe && window.parent) {
            // Новый чат создается в попапе, в шапку отправляем chatId, чтобы не показывать по нему уведомление
            window.parent.postMessage(
              {
                type: EEventQueueEvents.OpenNewChat,
                payload: { chatId: response.chat.chatId },
              },
              '*',
            );

            return;
          }

          return;
        }

        if (response.chat && response.chat.status === EStatus.Blocked) {
          telemetry.increment(ETelemetryEvents.ChatFirstMessageSendFail);

          batch(() => {
            dispatch(setFirstMessageChatsBlocked());
            dispatch(showAlert({ message: Messages[EFirstMessageStatus.ChatsBlocked] }));
          });

          return;
        }

        if (response.modalWarning) {
          telemetry.increment(ETelemetryEvents.ChatFirstMessageSendFail);
          telemetry.increment(ETelemetryEvents.ChatWsMessageGetModalWarning);
          batch(() => {
            dispatch(setFirstMessageValidationError());
            dispatch(
              showAlert({
                type: EAlert.MessageСontainsForbiddenText,
                message: parameters.text,
                modalWarning: response.modalWarning,
                chatId: BLOB_ID,
              }),
            );
          });
          trackOpenForbiddenTextPopup();

          return;
        }

        return;
      }

      telemetry.increment(ETelemetryEvents.ChatFirstMessageSendFail);

      const message = response.message;

      if (message === 'needCaptcha') {
        telemetry.increment(ETelemetryEvents.ChatFirstMessageGetCaptcha);
        dispatch(setFirstMessageNeedCaptcha());

        return;
      }

      if (message === 'needConfirmPhone') {
        batch(() => {
          dispatch(setFirstMessageNeedConfirmPhone());
          dispatch(showAlert({ message: Messages[EFirstMessageStatus.NeedConfirmPhone] }));
        });

        return;
      }

      if (message === 'userChatsDisabled') {
        batch(() => {
          dispatch(setFirstMessageUserChatsDisabled());
          dispatch(showAlert({ message: Messages[EFirstMessageStatus.ChatsDisabled] }));
        });

        return;
      }

      if (message === 'validationError' && 'errors' in response && response.errors && response.errors.length) {
        const isLinkError = response.errors.some(
          (error: { code: string }) => error.code === 'messageHasLinkToAnotherService',
        );

        if (isLinkError) {
          batch(() => {
            dispatch(setFirstMessageValidationError());
            dispatch(
              showAlert({ type: EAlert.MessageHasLinkToAnotherService, message: parameters.text, chatId: BLOB_ID }),
            );
          });
          trackOpenNoLinksPopup();

          return;
        }
      }

      batch(() => {
        dispatch(setFirstMessageFailed());
        dispatch(showAlert({ message: (response as IErrorResponseSchema).message }));
      });
    } catch (error) {
      logger.error(error, {
        domain: 'frontend-chats.actions.sendFirstMessage',
        description: 'Ошибка при отправке первого сообщения',
      });

      dispatch(showAlert({}));
    }
  };
